import {createContext, useContext, useState, useEffect, FC, ReactNode} from 'react'
import axios from 'axios'

// Criar instância do axios sem interceptors globais para o portal do cliente
// Isso evita conflitos com os interceptors do sistema principal
const clientPortalAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
})

export interface ClientPortalUser {
  id: number
  name: string
  email: string
  last_login_at?: string | null
  accesses: Array<{
    client_id: number
    instance_id: number
    client?: {
      id: number
      name: string
    } | null
    instance?: {
      id: number
      name: string
    } | null
  }>
}

export interface SelectedAccess {
  client_id: number
  instance_id: number
  client?: {
    id: number
    name: string
  } | null
  instance?: {
    id: number
    name: string
  } | null
}

interface ClientPortalAuthContextType {
  user: ClientPortalUser | null
  token: string | null
  loading: boolean
  selectedAccess: SelectedAccess | null
  setSelectedAccess: (access: SelectedAccess | null) => void
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const ClientPortalAuthContext = createContext<ClientPortalAuthContextType | undefined>(undefined)

export const useClientPortalAuth = () => {
  const context = useContext(ClientPortalAuthContext)
  if (!context) {
    throw new Error('useClientPortalAuth must be used within ClientPortalAuthProvider')
  }
  return context
}

interface ClientPortalAuthProviderProps {
  children: ReactNode
}

export const ClientPortalAuthProvider: FC<ClientPortalAuthProviderProps> = ({children}) => {
  const [user, setUser] = useState<ClientPortalUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedAccess, setSelectedAccessState] = useState<SelectedAccess | null>(null)

  // Helper function to auto-select if only one instance
  const autoSelectIfSingleInstance = (userData: ClientPortalUser | null) => {
    if (userData && userData.accesses && userData.accesses.length > 0) {
      const uniqueInstances = new Set(userData.accesses.map((acc: any) => acc.instance_id))
      if (uniqueInstances.size === 1) {
        // Only one instance - auto-select first access
        const firstAccess = userData.accesses[0]
        setSelectedAccessState(firstAccess)
        localStorage.setItem('client_portal_selected_access', JSON.stringify(firstAccess))
      }
    }
  }

  // Função para definir o acesso selecionado e salvar no localStorage
  const setSelectedAccess = (access: SelectedAccess | null) => {
    setSelectedAccessState(access)
    if (access) {
      localStorage.setItem('client_portal_selected_access', JSON.stringify(access))
    } else {
      localStorage.removeItem('client_portal_selected_access')
    }
  }

  // Carregar token, usuário e acesso selecionado do localStorage na inicialização
  useEffect(() => {
    const storedToken = localStorage.getItem('client_portal_token')
    const storedUser = localStorage.getItem('client_portal_user')
    const storedSelectedAccess = localStorage.getItem('client_portal_selected_access')

    if (storedToken && storedUser) {
      setToken(storedToken)
      try {
        const parsedUser = JSON.parse(storedUser)
        // Set user temporarily from localStorage (will be updated by verifyToken)
        setUser(parsedUser)
        
        // Se houver acesso selecionado salvo, restaurar temporariamente
        if (storedSelectedAccess) {
          try {
            const parsedAccess = JSON.parse(storedSelectedAccess)
            // Verificar se o acesso ainda é válido (existe nos acessos do usuário)
            const isValidAccess = parsedUser.accesses?.some(
              (acc: SelectedAccess) =>
                acc.client_id === parsedAccess.client_id &&
                acc.instance_id === parsedAccess.instance_id
            )
            if (isValidAccess) {
              setSelectedAccessState(parsedAccess)
            } else {
              localStorage.removeItem('client_portal_selected_access')
            }
          } catch (e) {
            console.error('Erro ao parsear acesso selecionado:', e)
            localStorage.removeItem('client_portal_selected_access')
          }
        }
      } catch (e) {
        console.error('Erro ao parsear usuário do localStorage:', e)
        localStorage.removeItem('client_portal_token')
        localStorage.removeItem('client_portal_user')
        localStorage.removeItem('client_portal_selected_access')
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
    // Note: verifyToken will be called in the next useEffect when token is set
  }, [])

  // Verificar token ao montar o componente e sempre que o token mudar
  // Isso garante que ao dar F5, os dados sejam atualizados do servidor
  useEffect(() => {
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]) // Executar quando o token mudar

  const verifyToken = async () => {
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const response = await clientPortalAxios.post(
        `/api/client-portal/auth/verify_token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data.success && response.data.user) {
        const updatedUser = response.data.user
        setUser(updatedUser)
        localStorage.setItem('client_portal_user', JSON.stringify(updatedUser))
        
        // Check if selected access is still valid
        if (selectedAccess) {
          const isValidAccess = updatedUser.accesses?.some(
            (acc: SelectedAccess) =>
              acc.client_id === selectedAccess.client_id &&
              acc.instance_id === selectedAccess.instance_id
          )
          
          if (!isValidAccess) {
            // Selected access is no longer valid, clear it
            setSelectedAccessState(null)
            localStorage.removeItem('client_portal_selected_access')
          }
        }
        
        // Auto-select logic:
        // 1. If there's only one access, auto-select it
        // 2. If all accesses are from the same instance, auto-select the first one
        if (updatedUser.accesses && updatedUser.accesses.length > 0) {
          const uniqueInstances = new Set(updatedUser.accesses.map((acc: any) => acc.instance_id))
          
          if (updatedUser.accesses.length === 1 || uniqueInstances.size === 1) {
            // Only one access OR all from same instance - auto-select
            if (!selectedAccess || !updatedUser.accesses.some(
              (acc: SelectedAccess) =>
                acc.client_id === selectedAccess.client_id &&
                acc.instance_id === selectedAccess.instance_id
            )) {
              const firstAccess = updatedUser.accesses[0]
              setSelectedAccessState(firstAccess)
              localStorage.setItem('client_portal_selected_access', JSON.stringify(firstAccess))
            }
          } else if (uniqueInstances.size > 1 && selectedAccess) {
            // Multiple instances - check if current selection is still valid
            const isValidSelection = updatedUser.accesses.some(
              (acc: SelectedAccess) =>
                acc.client_id === selectedAccess.client_id &&
                acc.instance_id === selectedAccess.instance_id
            )
            if (!isValidSelection) {
              // Current selection is invalid, clear it so user can choose again
              setSelectedAccessState(null)
              localStorage.removeItem('client_portal_selected_access')
            }
          }
        }
      } else {
        // Token inválido, limpar silenciosamente
        logout()
      }
    } catch (error: any) {
      // Ignorar erros de verificação de token silenciosamente
      // Se o token estiver inválido, apenas limpar sem mostrar erro
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout()
      } else {
        console.error('Erro ao verificar token:', error)
        logout()
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await clientPortalAxios.post(`/api/client-portal/auth/login`, {
        email,
        password,
      })

      if (response.data.success && response.data.api_token) {
        const newToken = response.data.api_token
        const newUser = response.data.user

        setToken(newToken)
        setUser(newUser)
        localStorage.setItem('client_portal_token', newToken)
        localStorage.setItem('client_portal_user', JSON.stringify(newUser))
        
        // Auto-select logic:
        // 1. If there's only one access, auto-select it
        // 2. If all accesses are from the same instance, auto-select the first one
        if (newUser.accesses && newUser.accesses.length > 0) {
          const uniqueInstances = new Set(newUser.accesses.map((acc: any) => acc.instance_id))
          
          if (newUser.accesses.length === 1 || uniqueInstances.size === 1) {
            // Only one access OR all from same instance - auto-select
            const firstAccess = newUser.accesses[0]
            setSelectedAccessState(firstAccess)
            localStorage.setItem('client_portal_selected_access', JSON.stringify(firstAccess))
          }
          // If multiple instances, don't auto-select - user must choose
        }
      } else {
        throw new Error('Resposta inválida do servidor')
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Erro ao realizar login'
      throw new Error(errorMessage)
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    setSelectedAccessState(null)
    localStorage.removeItem('client_portal_token')
    localStorage.removeItem('client_portal_user')
    localStorage.removeItem('client_portal_selected_access')

    // Chamar endpoint de logout se tiver token
    if (token) {
      clientPortalAxios.post(
        `/api/client-portal/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      ).catch(() => {
        // Ignorar erros no logout
      })
    }
  }

  const refreshUser = async () => {
    if (!token) return

    try {
      const response = await clientPortalAxios.get(`/api/client-portal/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success && response.data.user) {
        const updatedUser = response.data.user
        setUser(updatedUser)
        localStorage.setItem('client_portal_user', JSON.stringify(updatedUser))
        
        // Check if selected access is still valid
        if (selectedAccess) {
          const isValidAccess = updatedUser.accesses?.some(
            (acc: SelectedAccess) =>
              acc.client_id === selectedAccess.client_id &&
              acc.instance_id === selectedAccess.instance_id
          )
          
          if (!isValidAccess) {
            // Selected access is no longer valid, clear it
            setSelectedAccessState(null)
            localStorage.removeItem('client_portal_selected_access')
          }
        }
        
        // Auto-select logic:
        // 1. If there's only one access, auto-select it
        // 2. If all accesses are from the same instance, auto-select the first one
        if (updatedUser.accesses && updatedUser.accesses.length > 0) {
          const uniqueInstances = new Set(updatedUser.accesses.map((acc: any) => acc.instance_id))
          
          if (updatedUser.accesses.length === 1 || uniqueInstances.size === 1) {
            // Only one access OR all from same instance - auto-select
            if (!selectedAccess || !updatedUser.accesses.some(
              (acc: SelectedAccess) =>
                acc.client_id === selectedAccess.client_id &&
                acc.instance_id === selectedAccess.instance_id
            )) {
              const firstAccess = updatedUser.accesses[0]
              setSelectedAccessState(firstAccess)
              localStorage.setItem('client_portal_selected_access', JSON.stringify(firstAccess))
            }
          } else if (uniqueInstances.size > 1 && selectedAccess) {
            // Multiple instances - check if current selection is still valid
            const isValidSelection = updatedUser.accesses.some(
              (acc: SelectedAccess) =>
                acc.client_id === selectedAccess.client_id &&
                acc.instance_id === selectedAccess.instance_id
            )
            if (!isValidSelection) {
              // Current selection is invalid, clear it so user can choose again
              setSelectedAccessState(null)
              localStorage.removeItem('client_portal_selected_access')
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Erro ao atualizar dados do usuário:', error)
      // Se o token estiver inválido, fazer logout
      if (error?.response?.status === 401) {
        logout()
      }
    }
  }

  return (
    <ClientPortalAuthContext.Provider
      value={{
        user,
        token,
        loading,
        selectedAccess,
        setSelectedAccess,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </ClientPortalAuthContext.Provider>
  )
}

